import { useEffect, useState } from "react";
import { HeartPulse, Loader2, ShieldCheck, Lightbulb } from "lucide-react";
import client from "../../lib/api";
import { PageHeader, PrivacyBadge } from "./adminUi";

const BAND_COLOR = { steady: "#0E7A66", stretched: "#8A5808", needs_support: "#1D5FD4" };
const MOOD_BAND = {
  great: "steady", good: "steady", okay: "steady",
  tired: "stretched", anxious: "stretched",
  low: "needs_support", rough: "needs_support", worse: "needs_support",
};

export default function AdminMoodScreen() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get("/admin/mood").then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin" size={20} /></div>;
  if (!data) return <div className="k-card" style={{ padding: "2rem" }} data-testid="mood-error">We could not load the mood pulse.</div>;

  const total = data.total;

  return (
    <div data-testid="admin-mood">
      <PageHeader
        title="Mood pulse"
        subtitle="How the cohort is feeling, as a whole. Aggregate and care framed, never an individual."
        actions={<PrivacyBadge label="Aggregate, k=5. No individual mood." />}
      />

      {data.small_cohort ? (
        <div className="k-card" style={{ padding: "2.5rem", textAlign: "center" }} data-testid="mood-too-small">
          <HeartPulse size={26} className="mx-auto mb-3" style={{ color: "#1D5FD4" }} />
          <p className="k-quiet" style={{ maxWidth: "34rem", margin: "0 auto", lineHeight: 1.6 }}>
            A cohort under {data.k} people cannot be shown as a mood pulse without risking an individual. Come back when a few more have checked in.
          </p>
        </div>
      ) : (
        <>
          {data.headline && (
            <div className="k-card mb-6 flex items-start gap-3" style={{ padding: "1.25rem 1.5rem" }} data-testid="mood-headline">
              <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0E7A66", color: "white" }}><HeartPulse size={17} /></span>
              <p className="klareet-serif" style={{ fontSize: "1.15rem", color: "#131822", lineHeight: 1.45 }}>{data.headline}</p>
            </div>
          )}

          <div className="k-card mb-6" style={{ padding: "1.5rem" }} data-testid="mood-bands-card">
            <div className="text-sm font-medium mb-4" style={{ color: "#131822" }}>How the room feels</div>
            <div className="flex w-full h-8 rounded-lg overflow-hidden mb-4" data-testid="mood-bands-bar">
              {data.bands.filter((b) => !b.suppressed && b.count).map((b) => (
                <div
                  key={b.code}
                  title={`${b.label} ${Math.round((b.proportion || 0) * 100)}%`}
                  data-testid={`mood-band-seg-${b.code}`}
                  style={{ width: `${(b.proportion || 0) * 100}%`, background: BAND_COLOR[b.code], transition: "width 400ms ease" }}
                />
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {data.bands.map((b) => (
                <div key={b.code} className="flex items-center gap-2.5" data-testid={`mood-band-${b.code}`}>
                  <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: BAND_COLOR[b.code] }} />
                  <div>
                    <div className="text-sm" style={{ color: "#131822" }}>{b.label}</div>
                    <div className="klareet-mono text-xs" style={{ color: "rgba(19,24,34,0.6)" }}>
                      {b.suppressed ? "too few to report" : `${b.count} (${Math.round((b.proportion || 0) * 100)}%)`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="k-card" style={{ padding: "1.5rem" }} data-testid="mood-breakdown-card">
              <div className="text-sm font-medium mb-4" style={{ color: "#131822" }}>Mood breakdown</div>
              <div className="space-y-2.5">
                {data.moods.map((b) => {
                  const p = b.suppressed ? 0 : Math.round((b.proportion || 0) * 100);
                  return (
                    <div key={b.code} data-testid={`mood-row-${b.code}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <span style={{ color: "#131822" }}>{b.label}</span>
                        <span className="klareet-mono text-xs" style={{ color: "rgba(19,24,34,0.6)" }}>{b.suppressed ? "held back" : `${b.count} (${p}%)`}</span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(19,24,34,0.06)" }}>
                        <div style={{ width: `${p}%`, height: "100%", background: b.suppressed ? "rgba(19,24,34,0.15)" : BAND_COLOR[MOOD_BAND[b.code]], transition: "width 300ms ease" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {data.nudge && (
                <div className="k-card" style={{ padding: "1.5rem", background: "rgba(29,95,212,0.05)", borderColor: "rgba(29,95,212,0.2)" }} data-testid="mood-nudge">
                  <div className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: "#1D5FD4" }}><Lightbulb size={16} /> A gentle nudge</div>
                  <p className="text-sm" style={{ color: "#131822", lineHeight: 1.55 }}>{data.nudge}</p>
                </div>
              )}
              <div className="k-card flex items-start gap-3" style={{ padding: "1.25rem 1.5rem" }} data-testid="mood-care-note">
                <ShieldCheck size={18} style={{ color: "#0E7A66", marginTop: 2 }} />
                <p className="text-sm" style={{ color: "rgba(19,24,34,0.75)", lineHeight: 1.55 }}>{data.care_note}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
